import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGLTF } from '@react-three/drei';
import { resolveApiErrorMessage } from '@utils/apiErrorMessages';
import { loadGlbBlob } from '../../../services/cadGlbCache';

// React development StrictMode immediately runs effect cleanup/setup once.
// Delay URL disposal by one task so that simulated remount can cancel it,
// while a real unmount still releases the large GLB and useGLTF cache.
const pendingBlobRevocations = new Map<string, number>();

export interface GlbModelLoader {
  blobUrl: string | null;
  loading: boolean;
  loadError: string | null;
  /** Called by the in-Canvas Model once the GLB has been parsed and fitted. */
  markLoaded: () => void;
  retry: () => void;
}

/**
 * Owns the GLB byte load (through the session-scoped cadGlbCache, which sends the
 * auth header useGLTF cannot) and the per-load object-URL / drei GLTF-cache lifecycle.
 *
 * `onLoadStart` / `onClear` fire inside the fetch effect and are read through
 * refs so the effect's dependency list stays exactly [glbUrl, retryCount] — a
 * caller that re-creates the callbacks each render must not refetch the model,
 * and a language change must not reload the 3D model (PR #790).
 */
export function useGlbModelLoader({
  glbUrl,
  onLoadStart,
  onClear,
}: {
  glbUrl: string | null;
  onLoadStart: () => void;
  onClear: () => void;
}): GlbModelLoader {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const onLoadStartRef = useRef(onLoadStart);
  const onClearRef = useRef(onClear);
  useEffect(() => {
    onLoadStartRef.current = onLoadStart;
    onClearRef.current = onClear;
  }, [onClear, onLoadStart]);

  // Every load gets its own object URL, even for cached bytes, so revoking one
  // viewer's URL never breaks another viewer of the same model.
  useEffect(() => {
    let cancelled = false;
    const loadGlb = async () => {
      if (!glbUrl) {
        setBlobUrl(null);
        setLoadError(null);
        onClearRef.current();
        return;
      }

      setLoading(true);
      setLoadError(null);
      setBlobUrl(null);
      onLoadStartRef.current();

      try {
        const blob = await loadGlbBlob(glbUrl);
        const nextObjectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(nextObjectUrl);
        } else {
          setBlobUrl(nextObjectUrl);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(resolveApiErrorMessage(t, err, { fallbackKey: 'cad.viewer.loadFailed' }));
          setLoading(false);
        }
      }
    };

    loadGlb();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds the load-error message; excluded so a language change doesn't refetch/reload the 3D model (PR #790)
  }, [glbUrl, retryCount]);

  // Own one object URL per rendered model and release exactly that URL.
  useEffect(() => {
    if (!blobUrl) return undefined;

    const pendingRevocation = pendingBlobRevocations.get(blobUrl);
    if (pendingRevocation !== undefined) {
      window.clearTimeout(pendingRevocation);
      pendingBlobRevocations.delete(blobUrl);
    }

    return () => {
      const timer = window.setTimeout(() => {
        useGLTF.clear(blobUrl);
        URL.revokeObjectURL(blobUrl);
        pendingBlobRevocations.delete(blobUrl);
      }, 0);
      pendingBlobRevocations.set(blobUrl, timer);
    };
  }, [blobUrl]);

  return {
    blobUrl,
    loading,
    loadError,
    markLoaded: () => setLoading(false),
    retry: () => {
      setLoadError(null);
      setRetryCount((c) => c + 1);
    },
  };
}
